import { AiOutlineLoading } from "react-icons/ai";
import { Alert, Button } from "flowbite-react";
import useCEP from "../hooks/useCEP";
import { Controller, useForm } from "react-hook-form";
import { USERS } from "../config/apiConfig";
import axios from "../config/axiosInstance";
import { useCallback, useEffect, useState } from "react";
import { useApplication } from "../contexts/ApplicationContext";
import { useQuery } from "react-query";
import { HiAdjustments, HiUserCircle } from "react-icons/hi";
import { MdEmail } from "react-icons/md";
import { SiGoogle } from "react-icons/si";
import InputMask from "react-input-mask";
import AnimatedToast from "../components/common/AnimatedToast";
import { INTEGRATIONS } from "../config/apiConfig";
import { FaRegCalendarAlt } from "react-icons/fa";

const EMAIL_PROVIDERS = [
  { value: "", label: "Selecione o provedor" },
  { value: "locaweb", label: "Locaweb" },
  { value: "hostinger", label: "Hostinger" },
  { value: "hostgator", label: "HostGator" },
  { value: "uolhost", label: "UOL Host" },
  { value: "kinghost", label: "KingHost" },
  { value: "godaddy", label: "GoDaddy" },
  { value: "gmail", label: "Google Workspace / Gmail" },
  { value: "office365", label: "Outlook / Office 365" },
  { value: "zoho", label: "Zoho Mail" },
  { value: "other", label: "Outro / configuração manual" },
];

const cx = (...c) => c.filter(Boolean).join(" ");

const MyProfilePage = () => {
  const { user } = useApplication();

  const [activeTab, setActiveTab] = useState("profile");
  const [integrationToast, setIntegrationToast] = useState(null);
  const [calendarTokens, setCalendarTokens] = useState([]);
  const [tokensLoading, setTokensLoading] = useState(false);

  const [profileToast, setProfileToast] = useState(null);
  const [passwordAlert, setPasswordAlert] = useState({
    show: false,
    message: "",
    type: "",
  });
  const [emailAlert, setEmailAlert] = useState({
    show: false,
    message: "",
    type: "",
  });

  const [profileSubmitting, setProfileSubmitting] = useState(false);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [calendarConnecting, setCalendarConnecting] = useState(false);

  const [errorSubmit, setErrorSubmit] = useState(null);

  // ---- FORM: Perfil ----
  const {
    register: registerProfile,
    control: controlProfile,
    getValues: getProfileValues,
    handleSubmit: handleProfileSubmit,
    setValue: setProfileValue,
    formState: { errors: profileErrors },
  } = useForm();

  // ---- FORM: Senha ----
  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPasswordForm,
  } = useForm();

  // ---- FORM: E-mail ----
  const {
    register: registerEmail,
    handleSubmit: handleEmailSubmit,
    formState: { errors: emailErrors },
    reset: resetEmailForm,
  } = useForm();

  // ---- Buscar usuário ----
  const {
    isLoading,
    error,
    data: professional,
  } = useQuery(["professional", user], async () => {
    if (user?.id) {
      const { data: item } = await axios.get(`${USERS.GET_USER}/${user.id}`);
      return item.data;
    }
  });

  // ---- CEP / Endereço ----
  const {
    cep,
    setCep,
    address,
    loading: loadingCEP,
    error: errorCEP,
  } = useCEP();

  const onClickSearchCEP = () => {
    setCep(getProfileValues("addressPostalCode"));
  };

  useEffect(() => {
    if (!address) return;
    setProfileValue("address", address?.logradouro || "");
    setProfileValue("addressNeighborhood", address?.bairro || "");
    setProfileValue("addressCity", address?.localidade || "");
  }, [address, setProfileValue]);

  useEffect(() => {
    if (professional) {
      Object.keys(professional).forEach((key) => {
        setProfileValue(key, professional[key]);
      });
    }
  }, [professional, setProfileValue]);

  // ---- Submit: Perfil ----
  const onSubmitProfile = async (data) => {
    try {
      setProfileSubmitting(true);
      const payload = { ...data };
      delete payload.password;

      await axios.put(`${USERS.GET_USER}/${user.id}`, payload);

      setProfileToast({
        title: "Sucesso",
        message: "Informações atualizadas com sucesso!",
      });
    } catch (error) {
      setErrorSubmit(error);
      setProfileToast({
        title: "Erro",
        message: "Erro ao tentar atualizar informações!",
      });
    } finally {
      setProfileSubmitting(false);
      setTimeout(() => setProfileToast(null), 2500);
    }
  };

  // ---- Submit: Senha ----
  const onSubmitChangePassword = async (data) => {
    try {
      setPasswordSubmitting(true);

      if (data.password !== data.password_confirm) {
        throw new Error("Senhas divergentes");
      }

      const payload = {
        id: user.id,
        password: data.password,
      };

      await axios.put(`${USERS.GET_USER}/${user.id}`, payload);

      setPasswordAlert({
        show: true,
        message: "Senha atualizada com sucesso!",
        type: "success",
      });

      resetPasswordForm();
    } catch (error) {
      setErrorSubmit(error);
      setPasswordAlert({
        show: true,
        message: error?.message || "Erro ao tentar atualizar senha!",
        type: "failure",
      });
    } finally {
      setPasswordSubmitting(false);
      setTimeout(
        () =>
          setPasswordAlert({
            show: false,
            message: "",
            type: "",
          }),
        2500
      );
    }
  };

  // ---- Submit: Integração de e-mail ----
  const onSubmitEmailIntegration = async (data) => {
    try {
      setEmailSubmitting(true);

      if (!data.provider) {
        throw new Error("Selecione um provedor de e-mail.");
      }

      const payload = {
        provider: data.provider,
        email: data.email,
        password: data.password,
      };

      // TODO: trocar pelo endpoint real quando existir
      console.log("[EmailIntegration] payload:", payload);

      setEmailAlert({
        show: true,
        message: "Configuração de e-mail salva com sucesso!",
        type: "success",
      });

      resetEmailForm({
        provider: data.provider,
        email: data.email,
        password: "",
      });
    } catch (error) {
      setErrorSubmit(error);
      setEmailAlert({
        show: true,
        message:
          error?.message || "Erro ao tentar salvar a configuração de e-mail!",
        type: "failure",
      });
    } finally {
      setEmailSubmitting(false);
      setTimeout(
        () =>
          setEmailAlert({
            show: false,
            message: "",
            type: "",
          }),
        2500
      );
    }
  };

  const loadCalendarTokens = useCallback(async () => {
    setTokensLoading(true);
    try {
      const response = await axios.get(INTEGRATIONS.LIST_TOKENS);
      setCalendarTokens(response?.data?.data || []);
    } catch (error) {
      setIntegrationToast({
        title: "Erro",
        message:
          error?.response?.data?.message ||
          "Não foi possível buscar integrações.",
      });
    } finally {
      setTokensLoading(false);
    }
  }, []);

  const handleGoogleConnect = async () => {
    setCalendarConnecting(true);
    try {
      const response = await axios.get(INTEGRATIONS.GOOGLE_CALENDAR_URL);
      const authUrl = response?.data?.data?.url;
      if (!authUrl) throw new Error("URL de autorização não disponível.");
      window.open(authUrl, "_blank", "noopener,noreferrer");
      setIntegrationToast({
        title: "Atenção",
        message:
          "O Google Calendar abriu em nova aba. Após autorizar, volte nesta tela e clique em Atualizar status.",
      });
    } catch (error) {
      setIntegrationToast({
        title: "Erro",
        message:
          error?.response?.data?.message ||
          "Não foi possível iniciar a conexão com o Google Calendar.",
      });
    } finally {
      setCalendarConnecting(false);
    }
  };

  const handleDeleteToken = async (tokenType) => {
    try {
      await axios.delete(INTEGRATIONS.DELETE_TOKEN(tokenType));
      setIntegrationToast({
        title: "Sucesso",
        message: "Integração removida.",
      });
      loadCalendarTokens();
    } catch (error) {
      setIntegrationToast({
        title: "Erro",
        message:
          error?.response?.data?.message ||
          "Não foi possível remover a integração.",
      });
    }
  };

  useEffect(() => {
    loadCalendarTokens();
  }, [loadCalendarTokens]);

  return (
    <>
      <div className="w-full">
        <header className="flex flex-col gap-1 mb-4">
          <p className="text-sm uppercase tracking-[0.4em] text-orange-500">
            Configurações da conta
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">
                Meu Perfil
              </h1>
            </div>
          </div>
        </header>
        <div className="mx-auto bg-white px-4 py-5 md:px-8 md:py-7 rounded-xl shadow-sm border border-slate-100">
          {/* Abas customizadas – MOBILE: cards empilhados / DESKTOP: 3 colunas */}
          <div className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              {/* Perfil */}
              <button
                type="button"
                onClick={() => setActiveTab("profile")}
                className={cx(
                  "flex items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition",
                  activeTab === "profile"
                    ? "bg-orange-500 text-white border-orange-500 shadow-sm"
                    : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                )}
              >
                <span
                  className={cx(
                    "mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full text-sm",
                    activeTab === "profile"
                      ? "bg-slate-800 text-slate-50"
                      : "bg-white text-slate-500"
                  )}
                >
                  <HiUserCircle className="h-5 w-5" />
                </span>
                <span className="flex flex-col">
                  <span
                    className={cx(
                      "text-xs font-semibold uppercase tracking-wide",
                      activeTab === "profile"
                        ? "text-slate-50"
                        : "text-slate-700"
                    )}
                  >
                    Meu perfil
                  </span>
                  <span
                    className={cx(
                      "text-[11px] mt-0.5",
                      activeTab === "profile"
                        ? "text-slate-200"
                        : "text-slate-500"
                    )}
                  >
                    Nome, e-mail e dados básicos.
                  </span>
                </span>
              </button>

              {/* Senha */}
              <button
                type="button"
                onClick={() => setActiveTab("password")}
                className={cx(
                  "flex items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition",
                  activeTab === "password"
                    ? "bg-orange-500 text-white border-orange-500 shadow-sm"
                    : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                )}
              >
                <span
                  className={cx(
                    "mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full text-sm",
                    activeTab === "password"
                      ? "bg-slate-800 text-slate-50"
                      : "bg-white text-slate-500"
                  )}
                >
                  <HiAdjustments className="h-5 w-5" />
                </span>
                <span className="flex flex-col">
                  <span
                    className={cx(
                      "text-xs font-semibold uppercase tracking-wide",
                      activeTab === "password"
                        ? "text-slate-50"
                        : "text-slate-700"
                    )}
                  >
                    Trocar senha
                  </span>
                  <span
                    className={cx(
                      "text-[11px] mt-0.5",
                      activeTab === "password"
                        ? "text-slate-200"
                        : "text-slate-500"
                    )}
                  >
                    Atualize sua senha de acesso.
                  </span>
                </span>
              </button>

              {/* Integração de e-mail */}
              <button
                type="button"
                onClick={() => setActiveTab("email")}
                className={cx(
                  "flex items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition",
                  activeTab === "email"
                    ? "bg-orange-500 text-white border-orange-500 shadow-sm"
                    : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                )}
              >
                <span
                  className={cx(
                    "mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full text-sm",
                    activeTab === "email"
                      ? "bg-slate-800 text-slate-50"
                      : "bg-white text-slate-500"
                  )}
                >
                  <MdEmail className="h-5 w-5" />
                </span>
                <span className="flex flex-col">
                  <span
                    className={cx(
                      "text-xs font-semibold uppercase tracking-wide",
                      activeTab === "email" ? "text-slate-50" : "text-slate-700"
                    )}
                  >
                    Integração de e-mail
                  </span>
                  <span
                    className={cx(
                      "text-[11px] mt-0.5",
                      activeTab === "email"
                        ? "text-slate-200"
                        : "text-slate-500"
                    )}
                  >
                    Conecte sua conta de e-mail.
                  </span>
                </span>
              </button>
              {/* Google Calendar */}
              <button
                type="button"
                onClick={() => setActiveTab("calendar")}
                className={cx(
                  "flex items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition",
                  activeTab === "calendar"
                    ? "bg-orange-500 text-white border-orange-500 shadow-sm"
                    : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                )}
              >
                <span
                  className={cx(
                    "mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full text-sm",
                    activeTab === "calendar"
                      ? "bg-slate-800 text-slate-50"
                      : "bg-white text-slate-500"
                  )}
                >
                  <FaRegCalendarAlt className="h-5 w-5" />
                </span>
                <span className="flex flex-col">
                  <span
                    className={cx(
                      "text-xs font-semibold uppercase tracking-wide",
                      activeTab === "calendar"
                        ? "text-slate-50"
                        : "text-slate-700"
                    )}
                  >
                    Google Calendar
                  </span>
                  <span
                    className={cx(
                      "text-[11px] mt-0.5",
                      activeTab === "calendar"
                        ? "text-slate-200"
                        : "text-slate-500"
                    )}
                  >
                    Conecte sua agenda do Google.
                  </span>
                </span>
              </button>
            </div>
          </div>

          {/* Conteúdo das abas */}
          <div className="mt-2">
            {/* Aba: Perfil */}
            {activeTab === "profile" && (
              <form
                className="mb-10 space-y-4"
                onSubmit={handleProfileSubmit(onSubmitProfile)}
              >
                <Field label="Nome *" error={profileErrors?.name?.message}>
                  <input
                    type="text"
                    className={fieldClass}
                    {...registerProfile("name", { required: "Informe o nome" })}
                  />
                </Field>

                <Field label="E-mail *" error={profileErrors?.email?.message}>
                  <input
                    type="email"
                    className={fieldClass}
                    {...registerProfile("email", {
                      required: "Informe o e-mail",
                    })}
                  />
                </Field>

                <Field
                  label="Telefone"
                  error={profileErrors?.phoneNumber?.message}
                >
                  <Controller
                    name="phoneNumber"
                    control={controlProfile}
                    render={({ field }) => (
                      <InputMask
                        mask="(99) 99999-9999"
                        maskChar=""
                        {...field}
                        className={fieldClass}
                        placeholder=""
                      />
                    )}
                  />
                </Field>

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    className="w-full md:w-auto md:px-8 primary"
                    isProcessing={profileSubmitting}
                    processingSpinner={
                      <AiOutlineLoading className="h-6 w-6 animate-spin" />
                    }
                    disabled={profileSubmitting}
                  >
                    Atualizar perfil
                  </Button>
                </div>
              </form>
            )}

            {/* Aba: Senha */}
            {activeTab === "password" && (
              <form
                className="mb-10 space-y-4"
                onSubmit={handlePasswordSubmit(onSubmitChangePassword)}
              >
                <Field label="Nova senha *">
                  <input
                    type="password"
                    className={fieldClass}
                    {...registerPassword("password", {
                      required: "Informe a nova senha",
                    })}
                  />
                </Field>
                <Field label="Confirmar senha *">
                  <input
                    type="password"
                    className={fieldClass}
                    {...registerPassword("password_confirm", {
                      required: "Confirme a senha",
                    })}
                  />
                </Field>
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    className="w-full md:w-auto md:px-8 primary"
                    isProcessing={passwordSubmitting}
                    processingSpinner={
                      <AiOutlineLoading className="h-6 w-6 animate-spin" />
                    }
                    disabled={passwordSubmitting}
                  >
                    Atualizar senha
                  </Button>
                </div>
                {passwordAlert.show && (
                  <Alert className="mt-4" color={passwordAlert.type}>
                    <span className="font-medium">{passwordAlert.message}</span>
                  </Alert>
                )}
              </form>
            )}

            {/* Aba: Integração de e-mail */}
            {activeTab === "email" && (
              <div className="">
                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 md:p-6 shadow-sm">
                  <form
                    className="space-y-6"
                    onSubmit={handleEmailSubmit(onSubmitEmailIntegration)}
                  >
                    {/* Provedor */}
                    <div className="space-y-1.5">
                      <label className="block text-sm font-medium text-gray-700">
                        Provedor de e-mail *
                      </label>
                      <select
                        className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        {...registerEmail("provider", { required: true })}
                      >
                        {EMAIL_PROVIDERS.map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                      {emailErrors?.provider && (
                        <p className="mt-1 text-xs text-red-500">
                          Selecione um provedor de e-mail.
                        </p>
                      )}
                      <p className="mt-1 text-[11px] text-gray-500">
                        Escolha o provedor onde sua conta de e-mail foi criada
                        (Locaweb, Hostinger, HostGator, etc).
                      </p>
                    </div>

                    {/* Email + senha em grid */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <Field
                        label="E-mail da conta *"
                        error={emailErrors?.email?.message}
                      >
                        <input
                          type="email"
                          className={fieldClass}
                          {...registerEmail("email", {
                            required: "Informe um e-mail válido",
                          })}
                        />
                      </Field>
                      <Field
                        label="Senha do e-mail *"
                        error={emailErrors?.password?.message}
                      >
                        <input
                          type="password"
                          className={fieldClass}
                          {...registerEmail("password", {
                            required: "Informe a senha do e-mail",
                          })}
                        />
                      </Field>
                    </div>

                    {/* Botão */}
                    <div className="pt-1 flex justify-end">
                      <Button
                        type="submit"
                        className="w-full md:w-auto md:px-8 primary"
                        isProcessing={emailSubmitting}
                        processingSpinner={
                          <AiOutlineLoading className="h-6 w-6 animate-spin" />
                        }
                        disabled={emailSubmitting}
                      >
                        Salvar integração de e-mail
                      </Button>
                    </div>

                    {emailAlert.show && (
                      <Alert className="mt-3" color={emailAlert.type}>
                        <span className="font-medium">
                          {emailAlert.message}
                        </span>
                      </Alert>
                    )}
                  </form>
                </div>
              </div>
            )}

            {/* Aba: Google Calendar */}
            {activeTab === "calendar" && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-5 shadow-sm space-y-3">
                <div>
                  <p className="text-sm font-semibold text-slate-700">
                    Integração com Google Calendar
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Conecte seu calendário para sincronizar tarefas e
                    compromissos dentro do Relinq.
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  {!calendarTokens.length && (
                    <Button
                      className="flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#4285F4] via-[#34A853] to-[#EA4335] text-white"
                      onClick={handleGoogleConnect}
                      isProcessing={calendarConnecting}
                      processingSpinner={
                        <AiOutlineLoading className="h-5 w-5 animate-spin text-white" />
                      }
                    >
                      <div className="flex items-center gap-2">
                        <SiGoogle className="h-4 w-4" />
                        <span className="font-semibold text-sm">
                          Conectar com Google Calendar
                        </span>
                      </div>
                    </Button>
                  )}
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <p className="text-sm text-slate-700">
                    {calendarTokens.length ? (
                      <span className="text-green-500 font-semibold">
                        Integração ativa com Google Calendar.
                      </span>
                    ) : (
                      "Nenhuma integração configurada."
                    )}
                  </p>
                  {calendarTokens.length > 0 && (
                    <div className="mt-3 flex gap-2">
                      <Button
                        color="failure"
                        size="sm"
                        onClick={() =>
                          handleDeleteToken(calendarTokens[0].tokenType)
                        }
                      >
                        Desconectar Google Calendar
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <AnimatedToast
          open={Boolean(profileToast)}
          message={profileToast?.message}
          title={profileToast?.title}
          onClose={() => setProfileToast(null)}
        />
        <AnimatedToast
          open={Boolean(integrationToast)}
          message={integrationToast?.message}
          title={integrationToast?.title}
          onClose={() => setIntegrationToast(null)}
        />
      </div>
    </>
  );
};

const fieldClass =
  "block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:ring-0";

const Field = ({ label, error, children }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-[0.3em] text-slate-500">
        {label}
      </span>
      {error && <span className="text-[10px] text-rose-600">{error}</span>}
    </div>
    {children}
  </div>
);

export default MyProfilePage;
